import React, { useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Header from 'ui/atoms/header';
import Translate from 'ui/atoms/translate';
import Segment from 'ui/atoms/segment';
import { useApiCall } from 'hooks/use-api-call';
import { InvestorsApi, WizardApi } from 'api/apis';
import { useInvestorSelector } from 'apps/investor/resources/investor/hooks';
import { InvestorOverview } from 'api/models';
import Currency from 'ui/atoms/currency';
import LoadingRing from 'ui/atoms/loading-ring';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Col from 'ui/atoms/grid/col';
import ActionButtons from 'ui/molecules/action-buttons';
import Link from 'core/routing/link';
import Grid from 'ui/atoms/grid';
import OpenTransactions from './open-transactions';
import ContinueRegistration from './continue-registration';
import { useInvestorHasInvestmentsSelector } from 'apps/investor/resources/investments/hooks';
import * as Styled from './styled';
import InvestorInvestmentsTableView from '../../shared/investments-table-view';
import Section from 'src/ui/atoms/section';
import PageWrapper from 'src/core/layout/content/page-wrapper';

const InvestorOverviewPage = () => {
  const { investor: me, loading: investorLoading } = useInvestorSelector();
  const { hasInvestments } = useInvestorHasInvestmentsSelector();

  const { withApi, makeAuthenticatedApi, loading } = useApiCall(true);

  const [investorOverview, setInvestorOverview] = useState<InvestorOverview>();

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);

  const wizardApi: WizardApi = useMemo(() => makeAuthenticatedApi(WizardApi), [makeAuthenticatedApi]);

  const [filterPopupHeight, setFilterPopupHeight] = useState(0);

  useEffect(() => {
    if (!me?.id) {
      return;
    }
    withApi(async () => {
      const investorOverview = await investorsApi.investorsOverviewRetrieve({
        id: me.id,
      });
      setInvestorOverview(investorOverview);
    });
  }, [withApi, investorsApi, wizardApi, me?.id]);

  if (investorLoading || loading || !investorOverview) {
    return <LoadingRing />;
  }

  return (
    <PageWrapper paddingBottom={filterPopupHeight}>
      <WideContent title={<Translate name="dashboard.investorOverview" />}>
        <div>
          <Grid spacing="large">
            <Col width={4} tablet={12} phone={12} spacing="medium">
              <Segment raised={true}>
                <Header size="tiny" color="grayDark" spacing="medium" truncate={true}>
                  <Translate name="common.investments" />
                </Header>
                <Styled.HeaderRow>{investorOverview?.numberOfInvestments}</Styled.HeaderRow>
              </Segment>
            </Col>
            <Col width={4} tablet={12} phone={12} spacing="medium">
              <Segment raised={true}>
                <Header size="tiny" color="grayDark" spacing="medium" truncate={true}>
                  <Translate name="dashboardInvestorOverview.investmentSum" />
                </Header>
                <Styled.HeaderRow>
                  <Currency>{investorOverview?.totalInvestedSum}</Currency>
                </Styled.HeaderRow>
              </Segment>
            </Col>
            <Col width={4} tablet={12} phone={12} spacing="medium">
              <Segment raised={true}>
                <Header size="tiny" color="grayDark" spacing="medium" truncate={true}>
                  <Translate name="dashboardInvestorOverview.totalEarnings" />
                </Header>
                <Styled.HeaderRow>
                  <Currency>{investorOverview?.totalPayouts}</Currency>
                </Styled.HeaderRow>
              </Segment>
            </Col>
          </Grid>
        </div>

        {!hasInvestments && (
          <Section>
            <ContinueRegistration />
          </Section>
        )}
        {investorOverview?.numberOpenTransactions && (
          <Section>
            <OpenTransactions investorOverview={investorOverview} />
          </Section>
        )}

        <Header size="small" spacing="medium">
          <Translate name="dashboardInvestorOverview.lastInvestments" />
        </Header>
        <InvestorInvestmentsTableView
          defaultLimit={3}
          showProductColumn={true}
          availableLimits={[3, 10, 50, 100]}
          hidePaginationNavigation={true}
        />
        <ActionButtons spacingTop="small">
          <Link to="/dashboard/investorPortfolio">
            <Translate name="dashboardInvestorOverview.viewPortfolio" />
          </Link>
        </ActionButtons>
      </WideContent>
    </PageWrapper>
  );
};

export default WithAuthenticatedPage(InvestorOverviewPage, AppType.INVESTOR);
